import { Flex, Layout, Menu, message } from 'antd';
import { type MenuProps } from 'antd';
import React, { type FunctionComponentElement, useContext, useEffect, useState } from 'react';
import {
  TbClipboard,
  TbClipboardData,
  TbDatabaseImport,
  TbDoorExit,
  TbEye,
  TbHome,
  TbKey,
  TbLock,
  TbMailForward,
  TbPencil,
  TbPhoneCall,
  TbPhoneCalling,
  TbPhoneOutgoing,
  TbPlus,
  TbQuestionMark,
  TbReport,
  TbSettings,
  TbTag,
  TbTemplate,
  TbUser,
  TbUserCircle,
  TbUsers,
} from 'react-icons/tb';
import { Link, Outlet, type PathMatch, useLocation, useMatch } from 'react-router-dom';

import DocumentationButton from '~/components/DocumentationButton';
import LoginButton from '~/components/LoginButton';

import { useAuth } from '~/hooks/useAuth';
import DocumentationContext from '~/hooks/DocumentationContext';

import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import type { IAppSlice } from '~/redux/app/slice';

const { Content, Footer, Header, Sider } = Layout;

interface ValidatableMenuItem {
  icon?: FunctionComponentElement<any>;
  key: string;
  label: string;
  visible?: () => boolean;
  children?: ValidatableMenuItem[];
}

type MenuItem = Omit<ValidatableMenuItem, 'visible'>;

export default function AppLayout(): JSX.Element {
  const { hasAnyRole, hasRole, user } = useAuth();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { running, setDocumentation } = useContext(DocumentationContext);

  const [_messageApi, contextHolder] = message.useMessage();

  const { action, icon, title, version } = useAppSelector((state) => state.app);
  const call = useAppSelector((state) => state.pointage.call);
  const callLoaded = useAppSelector((state) => state.pointage.callLoaded);

  useEffect(() => {
    dispatch({ type: 'app/title', payload: undefined });
    dispatch({ type: 'app/icon', payload: undefined });
    dispatch({ type: 'app/action', payload: undefined });
    setDocumentation({});
  }, [location.pathname]);

  const menuTemplate: ValidatableMenuItem[] = [
    {
      icon: <TbHome />,
      key: '/',
      label: 'Accueil',
      visible: () => !callLoaded,
    },
    {
      icon: <TbPhoneCall />,
      key: `/campaigns/${call?.campaign?.humanId ?? ''}/calls/${call?.humanId ?? ''}`,
      label: 'Appel en cours',
      visible: () => callLoaded && !!call?.campaignId,
    },
    {
      icon: <TbPhoneCall />,
      key: `/groups/${call?.group?.humanId ?? ''}/calls/${call?.humanId ?? ''}`,
      label: 'Appel en cours',
      visible: () => callLoaded && !!call?.groupId,
    },
    {
      icon: <TbDoorExit />,
      key: `/?quit`,
      label: 'Quitter',
      visible: () => callLoaded,
    },
    {
      icon: <TbUser />,
      key: '/contacts/*',
      label: 'Contacts',
      visible: () => !callLoaded && hasAnyRole(['Admin', 'ContactsImport', 'Manager']),
      children: [
        {
          key: '/contacts/import',
          label: 'Importation',
          visible: () => hasRole('ContactsImport'),
        },
        {
          key: '/contacts',
          label: 'Liste',
        },
      ],
    },
    {
      icon: <TbClipboard />,
      key: '/campaigns/*',
      label: 'Campagnes',
      visible: () => !callLoaded && hasAnyRole(['Admin', 'Manager']),
      children: [
        {
          key: '/campaigns/new',
          label: 'Nouvelle',
        },
        {
          key: '/campaigns',
          label: 'Liste',
        },
      ],
    },
    {
      icon: <TbUsers />,
      key: '/groups/*',
      label: 'Groupes',
      visible: () => !callLoaded && hasAnyRole(['Admin', 'Manager', 'GroupsImport']),
      children: [
        {
          key: '/groups/import',
          label: 'Importation',
          visible: () => hasRole('GroupsImport'),
        },
        {
          key: '/groups/new',
          label: 'Nouveau',
          visible: () => hasRole('Admin'),
        },
        {
          key: '/groups',
          label: 'Liste',
        },
      ],
    },
    {
      icon: <TbTag />,
      key: '/tags/*',
      label: 'Étiquettes',
      visible: () => !callLoaded && hasAnyRole(['Admin', 'Manager']),
      children: [
        {
          key: '/tags/new',
          label: 'Nouvelle',
        },
        {
          key: '/tags',
          label: 'Liste',
        },
      ],
    },
    {
      icon: <TbReport />,
      key: '/reports',
      label: 'Rapports',
      visible: () => !callLoaded && hasAnyRole(['Admin', 'Manager']),
    },
    {
      icon: <TbLock />,
      key: '/roles/*',
      label: 'Rôles',
      visible: () => !callLoaded && hasRole('Admin'),
      children: [
        {
          key: '/roles/new',
          label: 'Nouveau',
        },
        {
          key: '/roles',
          label: 'Liste',
        },
      ],
    },
    {
      icon: <TbKey />,
      key: '/users/*',
      label: 'Utilisateurs',
      visible: () => !callLoaded && hasAnyRole(['Admin', 'UsersImport']),
      children: [
        {
          key: '/users/import',
          label: 'Importation',
          visible: () => hasRole('UsersImport'),
        },
        {
          key: '/users/new',
          label: 'Nouveau',
          visible: () => hasRole('Admin'),
        },
        {
          key: '/users',
          label: 'Liste',
          visible: () => hasRole('Admin'),
        },
      ],
    },
    {
      icon: <TbSettings />,
      key: '/configuration/*',
      label: 'Configuration',
      visible: () => !callLoaded && hasAnyRole(['Admin']),
      children: [
        {
          key: '/configuration/email_templates',
          label: 'Gabarits de courriels',
        },
        {
          key: '/configuration/source_emails',
          label: 'Courriels sources',
        },
        {
          key: '/configuration/source_phone_numbers',
          label: 'Numéros de téléphone sources',
        },
      ],
    },
  ];

  const Icons = {
    TbClipboard,
    TbClipboardData,
    TbDatabaseImport,
    TbDoorExit,
    TbEye,
    TbHome,
    TbKey,
    TbLock,
    TbMailForward,
    TbPencil,
    TbPhoneCall,
    TbPhoneCalling,
    TbPhoneOutgoing,
    TbPlus,
    TbQuestionMark,
    TbReport,
    TbTag,
    TbTemplate,
    TbUser,
    TbUserCircle,
    TbUsers,
  };

  const pathMatches = menuTemplate.reduce<Record<string, PathMatch<string>>>((acc, i) => {
    const match = useMatch(i.key);
    if (match) {
      acc[i.key] = match;
    }
    if (Array.isArray(i.children)) {
      return {
        ...acc,
        ...i.children.reduce<Record<string, PathMatch<string>>>((accChild, c) => {
          const matchChild = useMatch(c.key);
          if (matchChild) {
            accChild[c.key] = matchChild;
          }
          return accChild;
        }, {}),
      };
    }
    return acc;
  }, {});

  const mainMenu: MenuItem[] = [];
  for (const item of menuTemplate) {
    if (typeof item.visible === 'undefined' || item.visible()) {
      const { visible: _visible, ...rest } = item;
      mainMenu.push(rest);
      if (Array.isArray(rest.children)) {
        rest.children = rest.children
          .filter((c) => typeof c.visible === 'undefined' || c.visible())
          .map((c) => {
            const { visible: _v, ...restChild } = c;
            return restChild;
          });
      }
    }
  }

  const menuKeys: string[] = mainMenu.reduce<string[]>((acc, i) => {
    acc.push(i.key);

    if (i.children) {
      acc.push(...i.children.map((c) => c.key));
    }

    return acc;
  }, []);
  const selectedKeys: string[] = menuKeys.filter((key) => !!pathMatches[key]);

  const [collapsed, setCollapsed] = useState(false);
  const [openKeys, setOpenKeys] = useState(menuTemplate.filter((item) => !!pathMatches[item.key]).map((i) => i.key));
  const onOpenChange: MenuProps['onOpenChange'] = (keys) => {
    const latestOpenKey = keys.find((key) => !openKeys.includes(key));
    if (latestOpenKey && !mainMenu.map((i) => i.key).includes(latestOpenKey)) {
      setOpenKeys(keys);
    } else {
      setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
    }
  };

  const goto = (item: {
    domEvent: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>;
    key: string;
  }): void => {
    if (item.domEvent.ctrlKey) {
      window.open(item.key, '_blank');
      return;
    }

    if (location.pathname !== item.key) {
      dispatch({ type: 'app/goto', payload: item.key });
    }
  };

  function renderIcon(iconName: string): React.ReactElement | undefined {
    const i = Icons[iconName as keyof typeof Icons];
    if (!i) {
      return <TbQuestionMark />;
    }

    return React.createElement(i);
  }

  function renderAction(actionType: IAppSlice['action']): React.ReactElement | undefined {
    switch (actionType) {
      case 'add':
        return <TbPlus className='Layout__Header__Title__Action' />;
      case 'edit':
        return <TbPencil className='Layout__Header__Title__Action' />;
      case 'view':
        return <TbEye className='Layout__Header__Title__Action' />;
      case 'import':
        return <TbDatabaseImport className='Layout__Header__Title__Action' />;
      default:
        return undefined;
    }
  }

  function generateTitle(content: string | undefined, extra: string[] = []): string {
    if (!content) {
      return '';
    }

    const parts = content.split('|');
    return [...parts, ...extra].join(' — ');
  }

  function generateVersion(): string {
    if (['beta', 'development', 'dev'].includes(process.env.REACT_APP_CSN_ENV ?? '')) {
      return `v${version}-${process.env.REACT_APP_GIT_SHA ?? ''}`;
    }

    return `v${version}`;
  }

  return (
    <>
      <Layout className='Layout' style={{ pointerEvents: running ? 'none' : undefined }}>
        {contextHolder}
        <Header style={{ display: 'flex', alignItems: 'center' }} className='Layout__Header'>
          <div
            className='Layout__Header__Logo'
            onClick={() => {
              dispatch({ type: 'app/goto', payload: '/' });
            }}
          />
          <h1 className='Layout__Header__Title'>
            {icon && renderIcon(icon)}
            {action && renderAction(action)}
            {generateTitle(title)}
            &nbsp;
          </h1>
          {title === undefined && (
            <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'>
              <circle fill='#FFFFFF' stroke='#FFFFFF' strokeWidth='15' r='15' cx='40' cy='100'>
                <animate
                  attributeName='opacity'
                  calcMode='spline'
                  dur='2'
                  values='1;0;1;'
                  keySplines='.5 0 .5 1;.5 0 .5 1'
                  repeatCount='indefinite'
                  begin='-.4'
                ></animate>
              </circle>
              <circle fill='#FFFFFF' stroke='#FFFFFF' strokeWidth='15' r='15' cx='100' cy='100'>
                <animate
                  attributeName='opacity'
                  calcMode='spline'
                  dur='2'
                  values='1;0;1;'
                  keySplines='.5 0 .5 1;.5 0 .5 1'
                  repeatCount='indefinite'
                  begin='-.2'
                ></animate>
              </circle>
              <circle fill='#FFFFFF' stroke='#FFFFFF' strokeWidth='15' r='15' cx='160' cy='100'>
                <animate
                  attributeName='opacity'
                  calcMode='spline'
                  dur='2'
                  values='1;0;1;'
                  keySplines='.5 0 .5 1;.5 0 .5 1'
                  repeatCount='indefinite'
                  begin='0'
                ></animate>
              </circle>
            </svg>
          )}
          <span style={{ flexGrow: 1 }} />
          <div className='Layout__Header__Actions'>
            {user && <DocumentationButton />}
            <LoginButton />
          </div>
        </Header>
        <Content style={{ height: 'calc(100vh - 64px)' }}>
          <Layout style={{ minHeight: '100%' }}>
            <Sider
              collapsible
              collapsed={collapsed}
              onCollapse={(value) => {
                setCollapsed(value);
              }}
              style={{ backgroundColor: 'white' }}
            >
              <Menu
                className='Layout__Menu'
                items={mainMenu}
                mode='inline'
                motion={{
                  motionEnter: false,
                  motionLeave: false,
                }}
                onClick={goto}
                onOpenChange={onOpenChange}
                openKeys={openKeys}
                selectedKeys={selectedKeys}
              />
            </Sider>
            <Content className='Layout__Content'>
              <Flex vertical style={{ height: '100%' }}>
                <div className='Layout__Content__Inner'>
                  <Outlet />
                </div>
                <Footer className='Layout__Footer' style={{ textAlign: 'center' }}>
                  Pointage &mdash; CSN &mdash; <Link to='/changelog'>{generateVersion()}</Link>
                </Footer>
              </Flex>
            </Content>
          </Layout>
        </Content>
      </Layout>
    </>
  );
}
